import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import AddEmailAccountDialog from "./email/AddEmailAccountDialog";
import EditEmailAccountDialog from "./email/EditEmailAccountDialog";
import { EmailSettingsHeader } from "./email/EmailSettingsHeader";
import { EmailAccountItem } from "./email/EmailAccountItem";
import { EmailSignatureSettings } from "./email/EmailSignatureSettings";

const EmailSettings = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: accounts, isLoading } = useQuery({
    queryKey: ["emailAccounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_accounts")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleDeleteAccount = async (account: any) => {
    try {
      const { error } = await supabase
        .from("email_accounts")
        .delete()
        .eq("id", account.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["emailAccounts"] });
      toast({
        title: "Account Deleted",
        description: "Email account has been removed successfully",
      });
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Error",
        description: "Failed to delete email account",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EmailSettingsHeader onAddAccount={() => setIsAddDialogOpen(true)} />
      
      <div className="space-y-4">
        {accounts?.map((account) => (
          <EmailAccountItem
            key={account.id}
            account={account}
            onEdit={setSelectedAccount}
            onDelete={handleDeleteAccount}
          />
        ))}
        
        {accounts?.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            No email accounts connected yet
          </p>
        )}
      </div>

      <EmailSignatureSettings />

      <AddEmailAccountDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
      />
      
      {selectedAccount && (
        <EditEmailAccountDialog
          account={selectedAccount}
          open={!!selectedAccount}
          onOpenChange={(open) => !open && setSelectedAccount(null)}
        />
      )}
    </div>
  );
};

export default EmailSettings;